import { Component, OnInit } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ModuloEnum } from '../../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../../shared/enums/usuario/funcionalidade.enum';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../../../../../shared/services/apis/usuario/auth.service';
import { MaskService, Masks } from '../../../../../shared/services/mask.service';
import { UsuarioLoginResponse } from '../../../../../shared/services/responses/usuario/usuario-login.response';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { AiAgentService } from '../../../../../shared/services/apis/chat-ai/ai-agent.service';
import { ListaAiAgentResponse } from '../../../../../shared/services/responses/chat-ai/ai-agent.response';
import { WhatsappService } from '../../../../../shared/services/apis/chat-ai/whatsapp.service';
import { WhatsappRequest } from '../../../../../shared/services/requests/chat-ai/whatsapp.request';
import { EmpresaWhatsappStruct } from '../../../../../shared/services/structs/chat-ai/empresa-whatsapp.struct';
import { ReturnStruct } from '../../../../../shared/services/structs/return.struct';
import { WhatsappStruct } from '../../../../../shared/services/structs/chat-ai/whatsapp.struct';
import { WhatsappResponse } from '../../../../../shared/services/responses/chat-ai/whatsapp.response';
import { HeaderComponent } from '../../../../../shared/components/header/header.component';
import { ActionCardComponent } from '../../../../../shared/components/action-card/action-card.component';
import { FooterActionComponent } from '../../../../../shared/components/footer-action/footer-action.component';
import { CpfCnpjPipe } from '../../../../../shared/pipes/cpf-cnpj.pipe';

interface EmpresaParaCarregar {
  idEmpresa: number;
  nomeEmpresa: string;
  cnpj: string;
}

@Component({
  selector: 'app-whatsapp-cadastro',
  imports: [
    FormsModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSelectModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatExpansionModule,
    NgTemplateOutlet,
    NgxMaskDirective,
    CpfCnpjPipe,
    HeaderComponent,
    ActionCardComponent,
    FooterActionComponent,
    RouterModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: './whatsapp-cadastro.component.html',
  styleUrl: './whatsapp-cadastro.component.css',
})
export class WhatsappCadastroComponent implements OnInit {
  public isPageLoading: boolean = false;
  public moduloEnum: ModuloEnum;
  public funcionalidadeEnum: FuncionalidadeEnum;
  public model: FormGroup;
  public masks: Masks;

  public isMultiEmpresa: boolean = false;
  public btnSalvarDisabled: boolean = false;
  public userLoginResponse: UsuarioLoginResponse;
  public _listaAiAgentResponse: ListaAiAgentResponse;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private maskService: MaskService,
    private authService: AuthService,
    private aiAgentService: AiAgentService,
    private whatsappService: WhatsappService,
    private alertService: AlertService
  ) {
    this.moduloEnum = ModuloEnum.Home;
    this.funcionalidadeEnum = FuncionalidadeEnum.Home_Ferramentas;
  }

  async ngOnInit(): Promise<void> {
    this.masks = this.maskService.getMasks();

    this.model = this.formBuilder.group({
      empresas: this.formBuilder.array([]),
    });

    this.userLoginResponse = this.authService.getUserInfos();

    await this.popularDadosAiAgent();

    const empresasParaCarregar = this.resolverEmpresasParaCarregar();
    await this.popularDadosWhatsapp(empresasParaCarregar);
  }

  private resolverEmpresasParaCarregar(): EmpresaParaCarregar[] {
    const listaEmpresasDoGrupo = this.userLoginResponse.listaEmpresasDoGrupo;

    if (
      !listaEmpresasDoGrupo?.length ||
      !this.userLoginResponse.empresaPrincipalGrupo ||
      listaEmpresasDoGrupo.length === 1
    ) {
      this.isMultiEmpresa = false;
      return [
        {
          idEmpresa: this.userLoginResponse.idEmpresa,
          nomeEmpresa: this.userLoginResponse.nomeEmpresa,
          cnpj: this.userLoginResponse.cnpj,
        },
      ];
    }

    this.isMultiEmpresa = true;
    return listaEmpresasDoGrupo.map(empresa => ({
      idEmpresa: empresa.idEmpresa,
      nomeEmpresa: empresa.nomeEmpresa,
      cnpj: empresa.cnpj,
    }));
  }

  get empresas(): FormArray {
    return this.model.get('empresas') as FormArray;
  }

  listaNumerosDe(empresaGroup: AbstractControl): FormArray {
    return empresaGroup.get('listaNumeros') as FormArray;
  }

  private criarEmpresaGroup(empresa: EmpresaParaCarregar, listaWhatsapps: WhatsappStruct[]): FormGroup {
    return this.formBuilder.group({
      idEmpresa: [empresa.idEmpresa],
      nomeEmpresa: [empresa.nomeEmpresa],
      cnpj: [empresa.cnpj],
      listaNumeros: this.formBuilder.array((listaWhatsapps ?? []).map(item => this.criarItemWhatsappForm(item))),
    });
  }

  private criarItemWhatsappForm(item?: WhatsappStruct): FormGroup {
    return this.formBuilder.group({
      numero: [item?.numero || '', Validators.required],
      listaIdAiAgent: [item?.listaIdAiAgent ?? [], Validators.required],
    });
  }

  adicionarItemLista(indexEmpresa: number): void {
    this.listaNumerosDe(this.empresas.at(indexEmpresa)).push(this.criarItemWhatsappForm());
  }

  removerItemLista(indexEmpresa: number, indexNumero: number): void {
    this.listaNumerosDe(this.empresas.at(indexEmpresa)).removeAt(indexNumero);
  }

  async popularDadosWhatsapp(empresasParaCarregar: EmpresaParaCarregar[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const idsEmpresa = empresasParaCarregar.map(empresa => empresa.idEmpresa);

      this.whatsappService.getWhatsapp(this.isMultiEmpresa ? idsEmpresa : undefined).subscribe({
        next: (whatsappResponse: WhatsappResponse) => {
          if (whatsappResponse.isError) {
            this.alertService.show('Erro', whatsappResponse.errorDescription, AlertType.error);
            reject();
            return;
          }

          this.empresas.clear();
          empresasParaCarregar.forEach(empresa => {
            const bloco = whatsappResponse.listaEmpresaWhatsapps?.find(b => b.idEmpresa === empresa.idEmpresa);
            this.empresas.push(this.criarEmpresaGroup(empresa, bloco?.listaWhatsapps ?? []));
          });

          resolve();
        },
        error: error => {
          this.alertService.show('Erro inesperado', error, AlertType.error);
          reject();
        },
      });
    });
  }

  async salvaWhatsapp(whatsappRequest: WhatsappRequest): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.whatsappService.incluir(whatsappRequest).subscribe({
        next: (returnStruct: ReturnStruct) => {
          this.isPageLoading = false;
          if (returnStruct.isError) {
            this.alertService.show('Erro', returnStruct.errorDescription, AlertType.error);
            reject();
            return;
          }

          this.alertService.show('Sucesso', 'Dados salvos com sucesso.', AlertType.success);
          resolve();
        },
        error: error => {
          this.isPageLoading = false;
          this.alertService.show('Erro inesperado', error, AlertType.error);
          reject();
        },
      });
    });
  }

  async popularDadosAiAgent(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.aiAgentService.getAll().subscribe({
        next: (listaAiAgentResponse: ListaAiAgentResponse) => {
          if (listaAiAgentResponse.isError) {
            this.alertService.show('Erro', listaAiAgentResponse.errorDescription, AlertType.error);
            return;
          }

          if (listaAiAgentResponse.listaAiAgent != null) {
            this._listaAiAgentResponse = listaAiAgentResponse;
          }

          resolve();
        },
        error: error => {
          this.alertService.show('Erro inesperado', error, AlertType.error);
          reject();
        },
      });
    });
  }

  somenteNumeros(event: any): void {
    let inputValue = event.target.value;
    inputValue = inputValue.replace(/\D/g, '');
    event.target.value = inputValue;
  }

  voltar(): void {
    this.router.navigate(['/home/ferramentas']);
  }

  submit(): void {
    if (this.validaNumeroDuplicado()) {
      this.alertService.show('Erro', 'Não é permitido repetir números de WhatsApp na mesma empresa.', AlertType.error);
      return;
    }

    if (this.validaCamposPreenchidos()) {
      this.alertService.show('Erro', 'Todos os números e agentes devem ser preenchidos.', AlertType.error);
      return;
    }

    const whatsappRequest = new WhatsappRequest();
    whatsappRequest.listaEmpresaWhatsapps = this.empresas.controls.map(empresaGroup => {
      const empresaWhatsapp = new EmpresaWhatsappStruct();
      empresaWhatsapp.idEmpresa = empresaGroup.get('idEmpresa')?.value;
      empresaWhatsapp.listaWhatsapps = this.listaNumerosDe(empresaGroup).value;
      return empresaWhatsapp;
    });

    this.isPageLoading = true;
    this.salvaWhatsapp(whatsappRequest);
  }

  private validaNumeroDuplicado(): boolean {
    return this.empresas.controls.some(empresaGroup => {
      const numeros = this.listaNumerosDe(empresaGroup).value.map((x: { numero: string }) => x.numero?.trim());
      const numerosSet = new Set();

      for (const numero of numeros) {
        if (numerosSet.has(numero)) {
          return true;
        }
        numerosSet.add(numero);
      }

      return false;
    });
  }

  private validaCamposPreenchidos(): boolean {
    return this.empresas.controls.some(empresaGroup =>
      this.listaNumerosDe(empresaGroup).value.some(
        (item: any) =>
          !item.numero?.trim() || !Array.isArray(item.listaIdAiAgent) || item.listaIdAiAgent.length === 0
      )
    );
  }
}
